import { Intl as sparkIntl } from "genesys-spark";
import LOG from "services/loggingService";
import { DateTime } from "luxon";
import { Moment } from "moment";

/**
 * Type definitions for timeline formatting
 */
export type TimelineScale =
    | "millisecond"
    | "second"
    | "minute"
    | "hour"
    | "weekday"
    | "day"
    | "week"
    | "month"
    | "year";

export type TimelineLabelType = "minor" | "major";

/**
 * DateTime format options for "Fri, July 25, 2025 at 5:30:00 AM" format
 */
export const SHORT_WEEKDAY_FULL_DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
    weekday: "short",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
};

/**
 * DateTime format options for "Friday, July 25, 2025 at 5:30:00 AM" format
 */
export const FULL_DATE_MEDIUM_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
    dateStyle: "full",
    timeStyle: "medium",
};

/**
 * DateTime format options for "Friday, July 25, 2025 at 5:30 AM" format
 */
export const FULL_DATE_SHORT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
    dateStyle: "full",
    timeStyle: "short",
};

/**
 * DateTime format options for "July 25, 2025" format
 */
export const LONG_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    dateStyle: "long",
};

/**
 * DateTime format options for short dates "MM/DD/YYYY" format
 */
export const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
};

/**
 * Time format options for "m:ss.SSS" format
 */
export const TIME_MINUTES_SECONDS_MILLISECONDS_OPTIONS: Intl.DateTimeFormatOptions =
    {
        minute: "numeric",
        second: "2-digit",
        fractionalSecondDigits: 3,
    };

/**
 * Time format options for "m:ss" format
 */
export const TIME_MINUTES_SECONDS_OPTIONS: Intl.DateTimeFormatOptions = {
    minute: "numeric",
    second: "2-digit",
};

/**
 * Time format options for "H:mm:ss" format
 */
export const MEDIUM_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
    timeStyle: "medium",
    hour12: false,
};

/**
 * Timeline format options for minor and major labels
 * https://visjs.github.io/vis-timeline/docs/timeline/#Configuration_Options - Refer format option
 */

/**
 * Time format options for "HH:mm" format
 */
const HOUR_MINUTE_24H: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: false,
};

/**
 * Time format options for "ddd D MMMM" format
 */
const WEEKDAY_DAY_MONTH: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
    month: "long",
};

/**
 * Time format options for "MMMM YYYY" format
 */
const MONTH_YEAR: Intl.DateTimeFormatOptions = {
    month: "long",
    year: "numeric",
};

export const TIMELINE_MINOR_FORMAT_OPTIONS: Record<
    string,
    Intl.DateTimeFormatOptions
> = {
    millisecond: { fractionalSecondDigits: 3 }, // 'SSS'
    second: { second: "numeric" },
    minute: HOUR_MINUTE_24H,
    hour: HOUR_MINUTE_24H,
    weekday: { weekday: "short", day: "numeric" }, // 'ddd D'
    day: { day: "numeric" },
    month: { month: "short" }, // 'MMM',
    year: { year: "numeric" },
};

export const TIMELINE_MAJOR_FORMAT_OPTIONS: Record<
    string,
    Intl.DateTimeFormatOptions
> = {
    // 'HH:mm:ss'
    millisecond: {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    },
    // 'D MMMM HH:mm'  - "July 16 at 01:00"
    second: {
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    },
    minute: WEEKDAY_DAY_MONTH,
    hour: WEEKDAY_DAY_MONTH,
    weekday: MONTH_YEAR,
    day: MONTH_YEAR,
    week: MONTH_YEAR,
    month: { year: "numeric" },
};

/**
 * Helper class for standardized date and time operations using Spark's internationalization.
 * Provides consistent date parsing, validation, and formatting across the application.
 */
export class DateTimeHelper {
    /**
     * Gets the browser display locale from Spark's internationalization service.
     *
     * @returns {string} The locale string (e.g., 'en-US', 'en-GB') to use for date formatting
     */
    static getDisplayLocale(): string {
        try {
            const locale = sparkIntl.determineDisplayLocale();
            return locale || "en-US"; // fallback to en-US if null/undefined
        } catch (error) {
            LOG.warn(
                "Failed to determine display locale, falling back to en-US",
                error
            );
            return "en-US";
        }
    }

    /**
     * Validates and converts various date inputs to a native JavaScript Date object.
     * Handles Date objects, ISO strings, and null values with proper validation.
     *
     * @param {Date | string | null} date - The date input to validate and convert
     * @returns {Date | null} A valid Date object or null if input is invalid
     */
    static validateAndConvertDate(date: Date | string | null): Date | null {
        // Return null for falsy values (null, undefined, empty string)
        if (!date) {
            return null;
        }

        if (date instanceof Date) {
            // Check if Date object represents a valid date (NaN indicates invalid)
            if (isNaN(date.getTime())) {
                LOG.warn(`Invalid date value: ${date}`);
                return null;
            }
            return date;
        }

        if (typeof date === "string") {
            const parsedDate = this.parseIsoStringToDate(date, {
                setZone: true,
            });
            if (parsedDate) {
                return parsedDate.toJSDate();
            }
        }
        return null;
    }

    /**
     * Parses an ISO 8601 string and returns a Luxon DateTime object.
     * Provides precise control over timezone handling for advanced date operations.
     *
     * @param {string | null | undefined} isoString - The ISO 8601 string to parse (e.g., '2024-05-21T10:00:00Z')
     * @param {Object} [options={}] - Configuration options for parsing
     * @param {boolean} [options.setZone=false] - Whether to preserve the timezone from the input string
     * @returns {DateTime | null} Luxon DateTime object if valid; null if invalid
     * @example
     * // Parse with system timezone
     * DateTimeHelper.parseIsoStringToDate('2024-05-21T10:00:00Z')
     *
     * // Parse preserving original timezone
     * DateTimeHelper.parseIsoStringToDate('2024-05-21T10:00:00-05:00', { setZone: true })
     */
    static parseIsoStringToDate(
        isoString: string | null | undefined,
        options: { setZone?: boolean } = {}
    ): DateTime | null {
        const { setZone = false } = options;
        if (typeof isoString !== "string") {
            return null;
        }
        const dt = DateTime.fromISO(isoString, { setZone });

        return dt.isValid ? dt : null;
    }

    /**
     * Formats a date and time using Spark's internationalization with automatic locale detection.
     *
     * @param {Date | string | null} date - The date to format (Date object or ISO string)
     * @param {Intl.DateTimeFormatOptions} [options] - Intl formatting options (e.g., SHORT_WEEKDAY_FULL_DATETIME_OPTIONS)
     * @param {string} [timezone] - Optional timezone override (e.g., 'America/New_York', 'UTC')
     * @returns {string} Formatted date string or empty string if date is invalid
     * @example
     *  DateTimeHelper.formatDateWithTime('2023-07-02T19:30:00.000Z',SHORT_WEEKDAY_FULL_DATETIME_OPTIONS,'Asia/Kolkata')
     * 'Mon, July 3, 2023 at 1:00:00 AM'
     */
    static formatDateWithTime(
        date: Date | string | null,
        options?: Intl.DateTimeFormatOptions,
        timezone?: string
    ): string {
        const dateObject = this.validateAndConvertDate(date);
        if (!dateObject) return "";

        const formatOptions = timezone
            ? { ...options, timeZone: timezone }
            : options;
        return sparkIntl
            .dateTimeFormat(this.getDisplayLocale(), formatOptions)
            .format(dateObject);
    }

    /**
     * Calculates ISO week number for a given date
     *
     * @param {Date} date - The date to get week number for
     * @returns {number} Week number
     */
    private static getWeekNumber(date: Date): number {
        const d = new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
        );
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil(
            ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
        );
    }

    /**
     * Custom formatter for week numbers since Intl.DateTimeFormat doesn't support them
     *
     * @param {Date} date - The date to get week number for
     * @returns {string} Week number string
     */
    static formatWeekNumber(date: Date): string {
        try {
            if (!date || isNaN(date.getTime())) {
                LOG.warn("Invalid date provided to formatWeekNumber", { date });
                return "";
            }
            const weekNum = this.getWeekNumber(date);
            return weekNum.toString();
        } catch (error) {
            LOG.error("Error calculating week number", { date, error });
            return "";
        }
    }

    /**
     * Formats a timeline label using sparkIntl based on the scale type
     *
     * @param {Moment} momentDate - The moment date to format
     * @param {TimelineScale} scale - The timeline scale (millisecond, second, minute, etc.)
     * @param {TimelineLabelType} labelType - Either 'minor' or 'major'
     * @param {string} [timezone] - Optional timezone override
     * @returns {string} Formatted date string
     */
    static formatTimelineLabel(
        momentDate: Moment,
        scale: TimelineScale,
        labelType: TimelineLabelType,
        timezone?: string
    ): string | null {
        try {
            const locale = this.getDisplayLocale();
            if (!momentDate.isValid()) {
                return null;
            }

            // Handle week numbers specially for minor label
            if (scale === "week" && labelType === "minor") {
                return this.formatWeekNumber(momentDate.toDate());
            }

            const formatOptions =
                labelType === "minor"
                    ? TIMELINE_MINOR_FORMAT_OPTIONS[scale]
                    : TIMELINE_MAJOR_FORMAT_OPTIONS[scale];

            if (!formatOptions) {
                return "";
            }

            const options = timezone
                ? { ...formatOptions, timeZone: timezone }
                : formatOptions;

            return sparkIntl
                .dateTimeFormat(locale, options)
                .format(momentDate.toDate());
        } catch (error) {
            LOG.error("Error formatting timeline label", {
                momentDate: momentDate?.toString(),
                scale,
                labelType,
                timezone,
                error,
            });
            return null;
        }
    }

    /**
     * Creates vis-timeline format functions that use Intl.DateTimeFormat
     * https://visjs.github.io/vis-timeline/docs/timeline/#Configuration_Options - Refer format option
     *
     * @param {string} [timezone] - Optional timezone
     * @returns {Object} Object with minorLabels and majorLabels functions for vis-timeline
     */
    static createTimelineFormatFunctions(timezone?: string): {
        minorLabels: (date: Moment, scale: string) => string | null;
        majorLabels: (date: Moment, scale: string) => string | null;
    } {
        return {
            minorLabels: (date: Moment, scale: string) =>
                this.formatTimelineLabel(
                    date,
                    scale as TimelineScale,
                    "minor",
                    timezone
                ),
            majorLabels: (date: Moment, scale: string) =>
                this.formatTimelineLabel(
                    date,
                    scale as TimelineScale,
                    "major",
                    timezone
                ),
        };
    }
}
